import React, { useState, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DEFAULT_LEADS_ORDER_BY, useLeadsInfiniteQuery } from 'api/leads';
import useDebounce from 'helpers/hooks/useDebounce';

// material-ui
import {
  Fade,
  Card,
  Grid,
  InputAdornment,
  CircularProgress,
  withStyles,
  WithStyles,
  Button,
  Tooltip,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

// components
import TextField from 'components/TextField';
import IconButton from 'components/IconButton';
import LeadListFilter from 'components/LeadListFilter';

// icons
import Search from '@material-ui/icons/Search';
import Clear from '@material-ui/icons/Clear';

// selectors
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getCurrentUser from 'store/selectors/getCurrentUser';

//styles
import leadSearchBarStyles from 'layout/ApplicationLayout/components/LeadSearchBar/leadSearchBar.style';

export interface LeadSearchBarProps
  extends WithStyles<typeof leadSearchBarStyles> {}

const LeadSearchBar = ({ classes }: LeadSearchBarProps) => {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get('search') || '';

  const { isLoading: leadsLoading } = useLeadsInfiniteQuery();

  const currentGroupId = useSelector(getCurrentGroupId);
  const currentUser = useSelector(getCurrentUser);

  // State initializations
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [showSaveSnackbar, setShowSaveSnackbar] = useState(false);
  const [searchValue, setSearchValue] = useState(searchParam);
  const [focused, setFocused] = useState(false);
  const debouncedSearchValue = useDebounce(searchValue, 600);
  // Gate that blocks the search term from reaching the URL until saved filters have been applied
  const isInitializingRef = useRef(true);
  // Makes the saved-filters effect run once per entry into the leads list page
  const hasAppliedSavedFiltersRef = useRef(false);

  const handleClear = useCallback(() => setSearchValue(''), []);

  const handleSearchChange = useCallback(
    ({ target: { value } }) => setSearchValue(value),
    []
  );

  const handleFiltersSave = useCallback(() => {
    const savedQueryString = localStorage.getItem('leadsListQueries');
    const savedQueryList = savedQueryString
      ? JSON.parse(savedQueryString)
      : null;
    if (savedQueryList) {
      const existingQuery = savedQueryList.find(
        ({ groupId, accountId }: { groupId: number; accountId: number }) =>
          groupId.toString() === currentGroupId.toString() &&
          accountId.toString() === currentUser.toJS().id.toString()
      );
      if (existingQuery) {
        existingQuery.queryParams = searchParams.toString();
      } else {
        savedQueryList.push({
          groupId: currentGroupId,
          accountId: currentUser.toJS().id,
          queryParams: searchParams.toString(),
        });
      }
      localStorage.setItem('leadsListQueries', JSON.stringify(savedQueryList));
    } else {
      localStorage.setItem(
        'leadsListQueries',
        JSON.stringify([
          {
            groupId: currentGroupId,
            accountId: currentUser.toJS().id,
            queryParams: searchParams.toString(),
          },
        ])
      );
    }
    setShowSaveSnackbar(true);
    setTimeout(() => {
      setShowSaveButton(false);
      setShowSaveSnackbar(false);
    }, 3000);
  }, [searchParams, currentUser, currentGroupId]);

  const getExistingSavedQuery = useCallback(() => {
    const savedQueryString = localStorage.getItem('leadsListQueries');
    const savedQueryList = savedQueryString
      ? JSON.parse(savedQueryString)
      : null;
    if (savedQueryList) {
      return savedQueryList.find(
        ({ groupId, accountId }: { groupId: number; accountId: number }) =>
          groupId.toString() === currentGroupId.toString() &&
          accountId.toString() === currentUser.toJS().id.toString()
      );
    }
  }, [currentGroupId, currentUser]);

  // Sync the debounced search term into ?search=, which is what refetches the lead list.
  // Searching drops ?orderBy= because results come back relevance-ordered.
  useEffect(() => {
    if (isInitializingRef.current) return;
    const nextParams = new URLSearchParams(searchParams);
    if (debouncedSearchValue) {
      nextParams.set('search', debouncedSearchValue);
      nextParams.delete('orderBy');
    } else {
      nextParams.delete('search');
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [debouncedSearchValue, searchParams, setSearchParams]);

  // Apply the entry params (newLeads / uncontacted) or the user's saved filters, then open the gate.
  // Owns isInitializingRef: every path through here must end initialization.
  useEffect(() => {
    if (hasAppliedSavedFiltersRef.current) return;
    const existingQuery = getExistingSavedQuery();
    const params = new URLSearchParams(searchParams);
    const newLeads = params.get('newLeads');
    const uncontacted = params.get('uncontacted');

    if (newLeads) {
      params.delete('newLeads');
      setSearchParams(params, { replace: true });
    } else if (uncontacted) {
      setSearchParams(params, { replace: true });
    } else if (existingQuery) {
      setSearchParams(existingQuery.queryParams, { replace: true });
      const savedSearch =
        new URLSearchParams(existingQuery.queryParams).get('search') || '';
      setSearchValue(savedSearch);
    }

    hasAppliedSavedFiltersRef.current = true;
    isInitializingRef.current = false;
  }, [getExistingSavedQuery, searchParams, setSearchParams, pathname]);

  // Re-arm both refs on navigation into the leads list, since this app-wide bar never unmounts.
  // Skips the mount run, which would re-close the gate the effect above just opened.
  const previousPathnameRef = useRef(pathname);
  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (/\/leads(\/?|$)/i.test(pathname)) {
      hasAppliedSavedFiltersRef.current = false;
      isInitializingRef.current = true;
    }
  }, [pathname]);

  // Offer "Save Filters" only for a filter set worth saving — not for a plain search,
  // the default sort, or filters already saved
  useEffect(() => {
    const existingQuery = getExistingSavedQuery();
    const params = new URLSearchParams(searchParams);
    if (params.toString().includes('group')) params.delete('group');
    if (existingQuery) {
      if (
        existingQuery.queryParams === params.toString() ||
        params.toString().includes('search')
      ) {
        setShowSaveButton(false);
      } else setShowSaveButton(true);
    } else if (
      !params.toString() ||
      params.toString().includes('search') ||
      params.toString() === 'orderBy=' + DEFAULT_LEADS_ORDER_BY
    ) {
      setShowSaveButton(false);
    } else {
      setShowSaveButton(true);
    }
  }, [searchParams, getExistingSavedQuery]);

  return (
    <Grid container spacing={2} alignItems='center'>
      <Grid item xl={6} lg={7} md={8} sm={10}>
        <Card
          className={classNames(classes.searchBar, focused && classes.focused)}
        >
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={7} xl={8} className={classes.textField}>
              <TextField
                placeholder='Search Leads'
                value={searchValue}
                onChange={handleSearchChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                variant='standard'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                }}
              />
            </Grid>

            {leadsLoading ? (
              <Grid item xs={2} className={classes.barIcon}>
                <Fade in={leadsLoading}>
                  <CircularProgress size={20} />
                </Fade>
              </Grid>
            ) : (
              <Grid item xs={2} className={classes.barIcon}>
                <Fade in={Boolean(searchValue)}>
                  <IconButton id='clearButton' onClick={handleClear}>
                    <Clear />
                  </IconButton>
                </Fade>
              </Grid>
            )}

            <Grid item xs={3} xl={2} className={classes.filterIcon}>
              <LeadListFilter />
            </Grid>
          </Grid>
        </Card>
      </Grid>
      {showSaveButton && (
        <Grid item xs>
          <Tooltip
            id='leadListFiltersSave'
            title='Save applied filters as default'
          >
            <Button
              id='saveButton'
              color='primary'
              variant='contained'
              onClick={handleFiltersSave}
            >
              Save Filters
            </Button>
          </Tooltip>
          <Snackbar
            className={classes.filterSaveSnackbar}
            open={showSaveSnackbar}
            autoHideDuration={3000}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Alert variant='filled'>
              Your filters have been set as default for this browser
            </Alert>
          </Snackbar>
        </Grid>
      )}
    </Grid>
  );
};

export default withStyles(leadSearchBarStyles, { withTheme: true })(
  LeadSearchBar
);
